import styled, {css} from 'styled-components';
import React, {PropsWithChildren, Ref, forwardRef} from 'react';
import {Ripple} from '../Ripple/Ripple';
import {getBackgroundColor, getFontColor} from './helpers';
import {UnstyledLink} from '../UnstyledLink/UnstyledLink';
import {COLORS} from '../../../constants/colors';

export type ButtonType = 'primary' | 'secondary' | HTMLButtonElement['type'];

export interface ButtonProps {
  className?: string;
  grow?: boolean;
  type?: ButtonType;
  href?: string;
  disableShadow?: boolean;
  withBorder?: boolean;
  onClick?: React.MouseEventHandler;
  disabled?: boolean;
  ['data-testid']?: string;
  openExternalLinkInNewTab?: boolean;
}

export const Button = forwardRef<HTMLElement, PropsWithChildren<ButtonProps>>(
  function Button(
    {
      href,
      onClick,
      disabled,
      'data-testid': testId,
      openExternalLinkInNewTab,
      ...otherProps
    },
    ref,
  ) {
    if (href && !disabled) {
      const {className, ...rest} = otherProps;
      return (
        <UnstyledLink
          ref={ref as Ref<HTMLAnchorElement>}
          to={href}
          onClick={onClick}
          data-testid={testId}
          openExternalLinkInNewTab={openExternalLinkInNewTab}
          className={className}
        >
          <Ripple as={ButtonStyledAsSpan} {...rest} />
        </UnstyledLink>
      );
    }
    return (
      <Ripple
        ref={ref}
        as={StyledButton}
        onClick={onClick}
        disabled={disabled}
        data-testid={testId}
        aria-label={href ? undefined : otherProps.children}
        {...otherProps}
      />
    );
  },
);

const ButtonShadow = css`
  box-shadow: 0 3px 5px 0 transparent;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }

  &:hover,
  &:focus {
    box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2),
      0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12);

    transform: translateY(-2px);
  }
  &:active {
    transform: translateY(0px);
    box-shadow: 0px 5px 5px -3px rgba(0, 0, 0, 0.2),
      0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12);
  }
`;

export const StyledButton = styled.button<ButtonProps>`
  ${({type}) => css`
    background-color: ${getBackgroundColor(type)};
    color: ${getFontColor(type)};
  `};

  cursor: pointer;
  outline: none;
  margin: 0;

  font-size: 14px;
  font-weight: bold;
  text-align: center;
  border-radius: 4px;
  transition: box-shadow 280ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  text-decoration: none;

  display: inline-block;

  /* we have shadows unless we explicitly specify 'false' */
  ${({disableShadow}: ButtonProps) => (disableShadow ? '' : ButtonShadow)}

  border: ${({withBorder}: ButtonProps) =>
    withBorder ? `1px solid ${COLORS.Grey2}` : 0};

  ${({grow}: ButtonProps) =>
    grow
      ? css`
          padding: 16px 80px;
        `
      : css`
          padding: 16px 40px;
        `};

  &[disabled] {
    cursor: auto;
    filter: opacity(0.6) grayscale(0.85) brightness(1.1);
  }
`;

const ButtonStyledAsSpan = StyledButton.withComponent('span');
